'use client';
import { Button, InputText, Modal, ModalBody, ModalFooter, Select } from '@omnicomm/saigon-ui';
import { useForm } from '@tanstack/react-form';
import { HTTPError } from 'ky';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setCookie } from 'tiny-cookie';
import { useAuthorization } from '../model';
import type { DemoLang } from '../utils';
import { demoLogins } from '../utils';

const LANGUAGES = [
  ['ru', 'Русский'],
  ['en', 'English'],
  ['sp', 'Español'],
  ['pt', 'Português'],
  ['ch', '中文'],
  ['fr', 'Français'],
  ['vi', 'Tiếng Việt'],
  ['kz', 'Қазақша'],
];

type LoginFormFields = {
  login: string;
  password: string;
};

const defaultFormValues: LoginFormFields = {
  login: '',
  password: '',
};
export function LoginForm({ locale, whitelabelConfig }: { locale: DemoLang }) {
  const { t } = useTranslation('messages');
  const [authorizationError, setAuthorizationError] = useState('');
  const theme = whitelabelConfig?.theme as WhitelabelTheme;
  const { authorize: performAuthorization, isAuthorizationInProgress } = useAuthorization();
  const router = useRouter();

  const form = useForm({
    defaultValues: defaultFormValues,
    onSubmit: async (props) => {
      try {
        const { path } = await performAuthorization({ ...props.value, jwt: true });
        if (path) {
          router.push(path);
        }
      } catch (err: unknown) {
        if (!(err instanceof HTTPError)) {
          return setAuthorizationError('auth.unknownError');
        }
        const error = await err?.response?.json();
        if (error) {
          const status = error.status?.code || null;
          if (status === 1) {
            return setAuthorizationError('auth.user.notfound');
          }
          if (status === 2) {
            return setAuthorizationError('auth.user.blocked');
          }
          if (status === 3) {
            return setAuthorizationError('auth.user.deleted');
          }
          if (status === 5) {
            return setAuthorizationError('auth.wrongWl');
          }
          if (status === 6) {
            return setAuthorizationError('auth.serverDisabled');
          } else {
            return setAuthorizationError('auth.error-general');
          }
        }
      }
    },
  });

  useEffect(() => {
    form.validateAllFields('change');
  }, [form]);

  const getDemoLogin = (lang: DemoLang) => {
    return demoLogins[lang];
  };

  return (
    <>
      <Modal
        modalWidth={500}
        isOpen={!!authorizationError.length}
        onClose={() => setAuthorizationError('')}
        title={t('auth.authError')}
      >
        <ModalBody>{t(authorizationError)}</ModalBody>
        <ModalFooter>
          <Button
            dataTestId="login-error-ok"
            style={{
              backgroundColor: 'var(--primary-color)',
            }}
            onClick={() => setAuthorizationError('')}
          >
            OK
          </Button>
        </ModalFooter>
      </Modal>
      <form
        id="login-form"
        data-testid="login-form"
        className="tw:w-[300px] tw:sm:w-[340px]
         tw:self-center
         tw:hover:bg-[rgb(var(--login-form-background-color))]
         tw:transition-all tw:duration-300
           tw:bg-[rgb(var(--login-form-background-color)/var(--login-form-alpha-channel))]
            tw:flex tw:justify-center tw:flex-col tw:gap-[30px] tw:h-[440px] tw:p-10"
        onSubmit={(e) => {
          console.log(e);
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <h1
          className="tw:font-[400] tw:text-primary-200 tw:text-center tw:uppercase
         tw:font-etelka-medium tw:pb-[10px] tw:scale-[1.1] tw:scale-y-[.9] tw:text-[30px]
         login-form-logo
          "
        >
          {t(theme?.login?.form?.title)}
        </h1>
        <form.Field
          name="login"
          validators={{
            onChange: ({ value }) => (value?.length > 0 ? undefined : 'auth.loginRequired'),
          }}
        >
          {(fieldApi) => (
            <InputText
              size={260}
              name="login"
              dataTestId="login-input"
              canBeFocused
              borderColor={'#acacac'}
              customStyle={{
                backgroundColor: '#fff',
                padding: '11px 10px',
                fontSize: '14px',
              }}
              placeholder={t('auth.login')}
              value={fieldApi.state.value}
              onChange={({ value }) => fieldApi.handleChange(String(value))}
            />
          )}
        </form.Field>
        <form.Field
          name="password"
          validators={{
            onChange: ({ value }) => (value?.length > 0 ? undefined : 'auth.loginRequired'),
          }}
        >
          {(fieldApi) => (
            <InputText
              dataTestId="password-input"
              size={260}
              canBeFocused
              borderColor={'#acacac'}
              customStyle={{
                backgroundColor: '#fff',
                padding: '11px 10px',
                fontSize: '14px',
              }}
              placeholder={t('auth.password')}
              name="password"
              type="password"
              value={fieldApi.state.value}
              onChange={({ value }) => fieldApi.handleChange(String(value))}
            />
          )}
        </form.Field>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit]) => (
            <Button
              dataTestId="enter-submit"
              isLoading={isAuthorizationInProgress}
              isDisabled={isAuthorizationInProgress || !canSubmit}
              style={{
                margin: 0,
                height: 36,
                fontSize: 14,
                // TODO Придумать управление цветом кнопки через переменные
                backgroundColor: isAuthorizationInProgress || !canSubmit ? '#fc4f1699' : 'var(--accent-color)',
              }}
              onClick={() => form.handleSubmit()}
            >
              {t('auth.enter')}
            </Button>
          )}
        />

        <div className="tw:flex tw:justify-between">
          {!whitelabelConfig.hide_languages && (
            <Select
              dataTestId="language-select"
              size={110}
              IconStyle={{
                color: '#fff',
              }}
              caret={false}
              singleValueIcon={'Globe'}
              isClearable={false}
              value={locale}
              onChange={(option) => {
                setCookie('local', option);
                router.push('/');
              }}
              theme="dark"
              options={LANGUAGES.map((lang) => ({
                label: lang[1],
                value: lang[0],
              }))}
            />
          )}
          {!whitelabelConfig.hide_demo && (
            <Button
              dataTestId="demo-enter-submit"
              onClick={async () => {
                form.setFieldValue('login', () => getDemoLogin(locale));
                form.setFieldValue('password', () => 'rudemo123456');
                await form.validateAllFields('change');
                form.handleSubmit();
              }}
              style={{
                minWidth: 100,
                margin: 0,
                height: 28,
                fontSize: 13,
                backgroundColor: 'var(--primary-color-100)',
              }}
            >
              {t('auth.demoEnter')}
            </Button>
          )}
        </div>
      </form>
    </>
  );
}
